import React, { useEffect, useRef } from 'react';
import { Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader } from '@chakra-ui/react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import classNames from 'classnames';

import styles from './RightDrawer.module.scss';

interface RightDrawerProps {
  className?: string;
  headerStyle?: React.CSSProperties;
  headerTitle?: React.ReactNode;
  contentBody: JSX.Element;
  onClose(): void;
  width?: number;
  resizeSeparatorProps?: React.ComponentPropsWithoutRef<'hr'>;
  isResizing?: boolean;
}

export const RightDrawer: React.FC<RightDrawerProps> = ({
  className,
  headerTitle,
  contentBody,
  onClose,
  headerStyle,
  width,
  resizeSeparatorProps,
  isResizing,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const containerStyle = width
    ? ({ ['--rightDrawerWidth' as string]: `${width}px` } as React.CSSProperties)
    : undefined;

  useEffect(() => {
    const drawerContainer = containerRef.current;

    if (!drawerContainer) return;

    disableBodyScroll(drawerContainer);

    return () => {
      enableBodyScroll(drawerContainer);
    };
  }, [containerRef]);

  return (
    <div className={classNames(styles.container, className)} style={containerStyle}>
      <div className={styles.drawerContainer} ref={containerRef} />
      {resizeSeparatorProps && (
        <div
          {...resizeSeparatorProps}
          className={classNames(styles.resizer, { [styles.resizerActive]: isResizing })}
        />
      )}
      <Drawer
        isOpen={true}
        placement="right"
        size="md"
        onClose={onClose}
        isFullHeight={false}
        closeOnOverlayClick={true}
        portalProps={{ containerRef }}
        trapFocus={false} // Disable focus trapping to allow interaction with the main content
        // keep it disabled to prevent scroll issues
        blockScrollOnMount={false}
      >
        <DrawerContent className={styles.drawerContent} style={{ top: 'initial' }} data-testid="right-drawer-content">
          <DrawerHeader className={styles.header} style={headerStyle}>
            <DrawerCloseButton
              position="relative"
              right="2"
              top="initial"
              className={classNames('self-start', styles.closeButton)}
            />
            <div className={styles.title}>{headerTitle}</div>
          </DrawerHeader>
          <DrawerBody className={styles.body}>{contentBody}</DrawerBody>
        </DrawerContent>
      </Drawer>
    </div>
  );
};
